import { handleBootpayVerification } from './bootpayAuth'
import { toastUtil } from './toast'
import { authApi, RegisterResponse } from '../api/auth'
import type { RegisterRequest } from '../api/auth.types'
import { isRegisterDebugSkipPhoneVerification } from './registerDebugFeatures'

/** API·중복검사와 동일하게 숫자만 남긴다. */
export function normalizeRegisterPhoneInput(value: string): string {
  return value.replace(/\D/g, '')
}

async function completeRegisterWithPhone(
  registerData: Omit<RegisterRequest, 'phone'>,
  phoneRaw: string
): Promise<RegisterResponse | null> {
  // 인증·비인증 공통으로, 번호 정규화 → 중복 확인 → 가입 순서를 맞춘다.
  const verifiedPhoneNumber = normalizeRegisterPhoneInput(phoneRaw)

  try {
    const checkResponse = await authApi.checkPhone({
      phone: verifiedPhoneNumber,
    })

    if (!checkResponse.available) {
      toastUtil.error(
        checkResponse.message || '이미 사용 중인 전화번호입니다'
      )

      return null
    }

    const response = await authApi.register({
      ...(registerData.email ? { email: registerData.email } : {}),
      phone: verifiedPhoneNumber,
      username: registerData.username,
      ...(registerData.password ? { password: registerData.password } : {}),
      nickname: registerData.nickname,
      profile_image: registerData.profile_image,
      agree_terms: registerData.agree_terms,
      agree_privacy: registerData.agree_privacy,
      ...(registerData.provider &&
        registerData.provider_id && {
          provider: registerData.provider as any,
          provider_id: registerData.provider_id,
        }),
    })

    if (response.token) {
      localStorage.setItem('auth_token', response.token)
    }

    return response
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      '회원가입 중 오류가 발생했습니다'
    toastUtil.error(errorMessage)

    return null
  }
}

/**
 * 회원가입용 본인인증 전역 함수
 * 앱에서는 Flutter 네이티브 본인인증, 웹에서는 부트페이 JS SDK 사용.
 * 모달 없이 바로 부트페이 본인인증 창을 띄우고, 완료 후 회원가입을 진행합니다.
 *
 * @param registerData 회원가입에 필요한 데이터 (소셜 회원가입 시 password 생략)
 * @returns Promise<RegisterResponse | null> 성공 시 RegisterResponse, 실패/취소 시 null
 */
export const verifyPhoneForRegister = async (
  registerData: Omit<RegisterRequest, 'phone'>,
  options?: { debugPhone?: string }
): Promise<RegisterResponse | null> => {
  // development일 때만 부트페이 없이 입력 번호로 API만 호출한다.
  const debugPhoneRaw = options?.debugPhone?.trim()
  if (isRegisterDebugSkipPhoneVerification() && debugPhoneRaw) {
    const digits = normalizeRegisterPhoneInput(debugPhoneRaw)
    if (!digits) {
      toastUtil.error('휴대폰 번호를 입력해주세요')

      return null
    }

    return completeRegisterWithPhone(registerData, debugPhoneRaw)
  }

  return new Promise((resolve) => {
    handleBootpayVerification(
      async (verifiedPhoneNumber: string) => {
        const result = await completeRegisterWithPhone(
          registerData,
          verifiedPhoneNumber
        )
        resolve(result)
      },
      (errorMessage: string) => {
        toastUtil.error(errorMessage)
        resolve(null)
      },
      () => {
        toastUtil.info('본인인증이 취소되었습니다')
        resolve(null)
      }
    )
  })
}

/**
 * 비밀번호 찾기용 본인인증 전역 함수
 * 앱에서는 Flutter 네이티브 본인인증, 웹에서는 부트페이 JS SDK 사용.
 * 모달 없이 바로 부트페이 본인인증 창을 띄우고, 완료 후 전화번호를 반환합니다.
 *
 * @returns Promise<string | null> 성공 시 인증된 전화번호, 실패/취소 시 null
 */
export const verifyPhoneForPasswordFind = async (): Promise<string | null> => {
  return new Promise((resolve) => {
    handleBootpayVerification(
      async (verifiedPhoneNumber: string) => {
        toastUtil.success('본인인증이 완료되었습니다')
        resolve(verifiedPhoneNumber)
      },
      (errorMessage: string) => {
        toastUtil.error(errorMessage)
        resolve(null)
      },
      () => {
        toastUtil.info('본인인증이 취소되었습니다')
        resolve(null)
      }
    )
  })
}
